import { useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import Tesseract from "tesseract.js";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

/* =========================
   PARSEO FILENAME
========================= */
function parseFileName(fileName) {
  const clean = fileName.replace(".pdf", "").toLowerCase();
  const parts = clean.split(".");

  let contrato = null;
  let fechaParts = [];
  let sucursalParts = [];
  let tipo = null;

  for (let p of parts) {
    if (!contrato && /^\d{4,}$/.test(p)) {
      contrato = p;
      continue;
    }

    if (["mip"].includes(p)) {
      tipo = "MIP";
      continue;
    }

    if (/^\d{2,4}$/.test(p)) {
      fechaParts.push(p);
      continue;
    }

    if (/[a-z]/.test(p)) {
      sucursalParts.push(p);
    }
  }

  let fecha = null;

  if (fechaParts.length >= 3) {
    const [y, m, d] = fechaParts;
    if (y.length === 4) {
      fecha = `${d}/${m}/${y}`;
    }
  }

  return {
    contrato,
    fecha,
    sucursal: sucursalParts.join(" ") || null,
    tipo,
  };
}

/* =========================
   LIMPIEZA OCR
========================= */
function cleanOCR(text) {
  return text
    .toLowerCase()
    .replace(/[|\\\/]/g, "")
    .replace(/0/g, "o")
    .replace(/1/g, "l")
    .replace(/5/g, "s")
    .replace(/\s+/g, " ")
    .trim();
}

/* =========================
   NIVEL
========================= */
function extractNivel(text) {
  const t = cleanOCR(text);

  if (t.includes("actividad nula") || t.includes("sin actividad")) {
    return "nulo";
  }

  const match = t.match(/\bn\s*([1-3])\b/);
  if (match) return match[1];

  return "nulo";
}

/* =========================
   HORAS (POR CONTEXTO)
========================= */
function extractHoras(text) {
  const t = cleanOCR(text);

  function findHora(label) {
    const regex = new RegExp(
      label + "[^0-9]{0,30}(\\d{1,2}:\\d{2})",
      "i"
    );
    const match = t.match(regex);
    return match ? match[1] : null;
  }

  return {
    horaEntrada: findHora("entrada"),
    horaSalida: findHora("salida"),
  };
}

/* =========================
   PDF → IMÁGENES
========================= */
async function pdfToImages(file, onProgress) {
  const buffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;

  const images = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);

    const viewport = page.getViewport({ scale: 2 }); // ↑ calidad OCR

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({ canvasContext: ctx, viewport }).promise;

    images.push(canvas.toDataURL("image/png"));

    onProgress(Math.round((i / pdf.numPages) * 100));
  }

  return images;
}

/* =========================
   OCR
========================= */
async function runOCR(images, onProgress) {
  let fullText = "";

  for (let i = 0; i < images.length; i++) {
    const result = await Tesseract.recognize(images[i], "spa+eng", {
      logger: (m) => {
        if (m.status === "recognizing text") {
          onProgress(Math.round(m.progress * 100));
        }
      },
    });

    fullText += result.data.text + "\n\n";
  }

  return fullText;
}

/* =========================
   COMPONENTE
========================= */
export default function Dev() {
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [rawText, setRawText] = useState("");

  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    setPreview(url);

    const meta = parseFileName(file.name);

    setProgress(0);
    setData(null);
    setRawText("");

    // 1. PDF → imágenes
    const images = await pdfToImages(file, setProgress);

    // 2. OCR (lento pero preciso)
    const text = await runOCR(images, setProgress);

    // 3. extracción
    const nivel = extractNivel(text);
    const { horaEntrada, horaSalida } = extractHoras(text);

    setRawText(text);

    setData({
      ...meta,
      nivelPlaga: nivel,
      horaEntrada,
      horaSalida,
    });
  }

  return (
    <div className="devcont">
      <h1>Dev</h1>

      <input type="file" accept="application/pdf" onChange={handleFile} />

      {progress > 0 && progress < 100 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ height: 10, background: "#ccc" }}>
            <div style={{ height: 10, width: `${progress}%`, background: "#555" }} />
          </div>
          <small>{progress}%</small>
        </div>
      )}

      {preview && (
        <iframe
          src={preview}
          width="100%"
          height="400px"
          title="PDF Preview"
          style={{ marginTop: 10 }}
        />
      )}

      {data && (
        <div style={{ background: "#e0e0e0", padding: 10, marginTop: 10 }}>
          <h3>Hallazgo</h3>
          <p><strong>Contrato:</strong> {data.contrato || "-"}</p>
          <p><strong>Fecha:</strong> {data.fecha || "-"}</p>
          <p><strong>Sucursal:</strong> {data.sucursal || "-"}</p>
          <p><strong>Tipo:</strong> {data.tipo || "-"}</p>
          <p><strong>Hora entrada:</strong> {data.horaEntrada || "-"}</p>
          <p><strong>Hora salida:</strong> {data.horaSalida || "-"}</p>
          <p><strong>Nivel de plaga:</strong> {data.nivelPlaga}</p>
        </div>
      )}

      {rawText && (
        <div
          style={{
            background: "#111",
            color: "#0f0",
            padding: 10,
            marginTop: 20,
            whiteSpace: "pre-wrap",
            fontSize: 12,
            maxHeight: 300,
            overflowY: "auto",
          }}
        >
          <strong>Texto OCR (debug):</strong>
          <br />
          {rawText}
        </div>
      )}
    </div>
  );
}